import { Mail, Phone, MapPin, MessageCircle } from "lucide-react";
import { BRAND } from "@/data/site";
import { useSeo } from "@/lib/seo";
import Breadcrumbs from "@/components/Breadcrumbs";
import LeadCaptureForm from "@/components/LeadCaptureForm";

export default function ContactPage() {
  useSeo({
    title: "Contact GrabNChat – Book a WhatsApp Business API Demo",
    description: "Talk to the GrabNChat team about the WhatsApp Business API, pricing, a free demo, support or partnerships. Call, email or send us a message.",
  });

  return (
    <div data-testid="contact-page">
      <section className="pt-8 md:pt-10 pb-10 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"><Breadcrumbs items={[{ name: "Contact", path: "/contact" }]} /></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center pt-12 md:pt-14">
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900">Let’s talk about WhatsApp for your business</h1>
          <p className="mt-4 text-lg text-slate-600 max-w-2xl mx-auto">Sales, support or partnerships—we’re a message away.</p>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7 bg-white border border-slate-200 rounded-2xl p-6 sm:p-8">
            <LeadCaptureForm testId="contact-form" />
          </div>

          <div className="lg:col-span-5 space-y-4">
            <InfoCard icon={Mail} label="Email" value={BRAND.email} href={`mailto:${BRAND.email}`} testId="contact-info-email" />
            <InfoCard icon={Phone} label="Phone" value={BRAND.phone} href={`tel:${BRAND.phone.replace(/\s/g, "")}`} testId="contact-info-phone" />
            <InfoCard icon={MessageCircle} label="WhatsApp" value={`+${BRAND.whatsapp}`} href={`https://wa.me/${BRAND.whatsapp}`} testId="contact-info-whatsapp" />
            <InfoCard icon={MapPin} label="Office" value={BRAND.address} testId="contact-info-address" />
            <div className="rounded-2xl overflow-hidden border border-slate-200">
              <iframe
                title="GrabNChat Office"
                src="https://www.google.com/maps?q=Aggarwal+City+Plaza+Mangalam+Place+Rohini+New+Delhi+110085&output=embed"
                className="w-full h-72"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function InfoCard({ icon: Icon, label, value, href, testId }) {
  const content = (
    <div className="flex items-start gap-3 p-5 bg-white border border-slate-200 rounded-2xl hover:border-[#25D366] transition-colors mt-3" data-testid={testId}>
      <div className="w-11 h-11 rounded-xl bg-[#25D366]/10 text-[#25D366] grid place-items-center"><Icon className="w-5 h-5" /></div>
      <div>
        <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
        <div className="text-base font-semibold text-slate-900">{value}</div>
      </div>
    </div>
  );
  return href ? <a href={href} target="_blank" rel="noreferrer">{content}</a> : content;
}
